import { createStore, type StoreApi } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';
import type { Result } from '@tessa-lean/base';
import type {
  GitDiffQuery,
  GitDiffResponse,
  GitOverviewResponse,
} from '@tessa-lean/base/api-schemas/index.js';

import { gitApi as defaultGitApi } from '../api/clients/git.js';
import type { ApiContext, ApiError } from '../api/client.js';

export interface GitSliceState {
  gitOverview: GitOverviewResponse | null;
  gitOverviewLoading: boolean;
  gitOverviewError: string | null;
  gitDiff: GitDiffResponse | null;
  gitDiffLoading: boolean;
  gitDiffError: string | null;
  gitRepositoryLinkSaving: boolean;
}

export const GIT_INITIAL_STATE: GitSliceState = {
  gitOverview: null,
  gitOverviewLoading: false,
  gitOverviewError: null,
  gitDiff: null,
  gitDiffLoading: false,
  gitDiffError: null,
  gitRepositoryLinkSaving: false,
};

export interface GitSliceActions {
  loadGitOverview: () => Promise<Result<GitOverviewResponse, ApiError>>;
  loadGitDiff: (query?: GitDiffQuery) => Promise<Result<GitDiffResponse, ApiError>>;
  saveGitRepositoryLink: (url: string | null) => Promise<Result<string | null, ApiError>>;
  _resetGit: () => void;
}

export type GitSlice = GitSliceState & GitSliceActions;

export interface GitSliceConfig {
  api?: typeof defaultGitApi;
  apiContext?: ApiContext;
}

function defaultApiContext(): ApiContext {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('tessaLeanToken') : null;
  return { baseUrl: '', token };
}

export const createGitSlice =
  (config: GitSliceConfig = {}): StateCreator<GitSlice> =>
  (set) => {
    const api = config.api ?? defaultGitApi;
    const ctx = config.apiContext ?? defaultApiContext();
    return {
      ...GIT_INITIAL_STATE,

      loadGitOverview: async () => {
        set({ gitOverviewLoading: true, gitOverviewError: null });
        const result = await api.getOverview(ctx);
        if (result.ok) {
          set({
            gitOverview: result.value,
            gitOverviewLoading: false,
            gitOverviewError: null,
          });
        } else {
          set({
            gitOverviewLoading: false,
            gitOverviewError: result.error.message,
          });
        }
        return result;
      },

      loadGitDiff: async (query = {}) => {
        set({ gitDiffLoading: true, gitDiffError: null });
        const result = await api.getDiff(ctx, query);
        if (result.ok) {
          set({
            gitDiff: result.value,
            gitDiffLoading: false,
            gitDiffError: null,
          });
        } else {
          set({
            gitDiffLoading: false,
            gitDiffError: result.error.message,
          });
        }
        return result;
      },

      saveGitRepositoryLink: async (url) => {
        set({ gitRepositoryLinkSaving: true });
        const result = await api.saveRepositoryLink(ctx, url);
        if (result.ok) {
          set((state) => ({
            gitRepositoryLinkSaving: false,
            gitOverview:
              state.gitOverview === null
                ? state.gitOverview
                : { ...state.gitOverview, repositoryLink: result.value.url },
          }));
          return { ok: true, value: result.value.url } satisfies Result<string | null, ApiError>;
        }
        set({ gitRepositoryLinkSaving: false });
        return result;
      },

      _resetGit: () => {
        set({ ...GIT_INITIAL_STATE });
      },
    };
  };

export function createGitStore(config: GitSliceConfig = {}): StoreApi<GitSlice> {
  return createStore<GitSlice>()(createGitSlice(config));
}
